import { Typography } from "antd";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { FALLBACK_LOCALE, isSupportedLocale } from "../i18n";
import type { SupportedLocale } from "../i18n";

const { Paragraph } = Typography;

/**
 * The text of the privacy policy, one block per language.
 *
 * It is the wording of the old application's `main/_privacy_policy.<lang>.html.erb`
 * partials, carried over verbatim — this is the statement users have consented
 * to, so it is not paraphrased or merged into a single "translatable" version:
 * the three texts genuinely differ (the Italian one opens with an extra
 * sentence, and each links to its own jw.org page).
 *
 * It lives in the client rather than in the Rails locales because it is page
 * content, not a label: the API has no views, and the "here" link has to call
 * back into the component that opens the personal-data dialog.
 */
interface PrivacyPolicyTextProps {
  onShowPersonalData: () => void;
}

function PersonalDataLink({
  onClick,
  children,
}: {
  onClick: () => void;
  children: string;
}) {
  return (
    <Typography.Link
      onClick={(event) => {
        event.preventDefault();
        onClick();
      }}
    >
      {children}
    </Typography.Link>
  );
}

function De({ onShowPersonalData }: PrivacyPolicyTextProps) {
  return (
    <>
      <Paragraph>
        Auf der myspeechplan Website werden die folgenden personenbezogenen Daten verarbeitet:
      </Paragraph>
      <ul>
        <li>Vorname</li>
        <li>Name</li>
        <li>Adresse (fakultativ - nur wenn angegeben)</li>
        <li>Telefonnummern</li>
        <li>E-Mail-Adresse</li>
        <li>Versammlung / Kreis</li>
        <li>Vortrags-Schemas</li>
        <li>Aus Sicherheitsgründen die IP-Adresse</li>
      </ul>
      <Paragraph>
        Das Geburtsdatum und das Taufdatum werden vom System nur für die Validierung verwendet und
        somit nicht persistent gespeichert.
      </Paragraph>
      <Paragraph>
        Die persönlichen Daten können{" "}
        <PersonalDataLink onClick={onShowPersonalData}>hier</PersonalDataLink> kontrolliert werden,
        Anpassungen können in den Einstellungen vorgenommen werden (oder über den Support).
      </Paragraph>
      <Paragraph>
        Hast Du über das Formular S-290 die Einwilligung bezüglich der Verarbeitung
        personenbezogener Daten erteilt (siehe{" "}
        <Typography.Link
          href="https://www.jw.org/de/datenschutzerklaerung/"
          target="_blank"
          rel="noreferrer"
        >
          Datenschutzerklärung
        </Typography.Link>
        )? Sollte das Formular S-290 nicht akzeptiert worden sein, so muss dein Account gelöscht
        werden.
      </Paragraph>
    </>
  );
}

function En({ onShowPersonalData }: PrivacyPolicyTextProps) {
  return (
    <>
      <Paragraph>The following personal data is processed on the website:</Paragraph>
      <ul>
        <li>First name</li>
        <li>Last name</li>
        <li>Address (optional - only if indicated)</li>
        <li>Phone numbers</li>
        <li>E-Mail Address</li>
        <li>Congregation / Circumscription</li>
        <li>Speech schemas</li>
        <li>For security reasons the IP address</li>
      </ul>
      <Paragraph>
        The date of birth and the date of baptism are only used by the system for validation and are
        therefore not stored persistently.
      </Paragraph>
      <Paragraph>
        You can verify your personal data{" "}
        <PersonalDataLink onClick={onShowPersonalData}>here</PersonalDataLink>, changes can be done
        in the settings sections (or contact the support).
      </Paragraph>
      <Paragraph>
        Have you given your consent to the processing of personal data via the form S-290 (see{" "}
        <Typography.Link
          href="https://www.jw.org/en/privacy-policy/"
          target="_blank"
          rel="noreferrer"
        >
          Privacy Policy
        </Typography.Link>
        )? If you have not accepted the S-290 form, your account will have to be deactivated.
      </Paragraph>
    </>
  );
}

function It({ onShowPersonalData }: PrivacyPolicyTextProps) {
  return (
    <>
      <Paragraph>
        Nel rispetto delle normative della privacy, chiediamo di visualizzare i vostri dati e di
        dare il consenso al trattamento dei dati sul sito myspeechplan. Sul sito vengono trattati i
        seguenti dati personali:
      </Paragraph>
      <ul>
        <li>Nome</li>
        <li>Cognome</li>
        <li>CAP e nazione (facoltativo - solo se indicato)</li>
        <li>Numeri di telefono</li>
        <li>Indirizzo E-Mail</li>
        <li>Congregazione / Circoscrizione</li>
        <li>Schemi dei discorsi</li>
        <li>indirizzo IP per motivi di sicurezza</li>
      </ul>
      <Paragraph>
        La data di nascita e di battesimo vengono usate solo come validazione, le date stesse non
        vengono registrate nel sistema.
      </Paragraph>
      <Paragraph>
        I propri dati personali possono essere verificati{" "}
        <PersonalDataLink onClick={onShowPersonalData}>qui</PersonalDataLink>, in caso di modifiche
        vedere le impostazioni o contattare il support.
      </Paragraph>
      <Paragraph>
        Hai accettato il modulo S-290 per il consenso al trattamento dei dati personali (vedi{" "}
        <Typography.Link
          href="https://www.jw.org/it/informativa-privacy/trattamento-dati-personali/"
          target="_blank"
          rel="noreferrer"
        >
          Trattamento dei dati personali
        </Typography.Link>
        )? Se non hai firmato il modulo S-290, rivolgiti al tuo segretario, altrimenti il tuo
        account myspeechplan dovrà essere disattivato.
      </Paragraph>
    </>
  );
}

const TEXTS: Record<SupportedLocale, (props: PrivacyPolicyTextProps) => ReactElement> = {
  de: De,
  en: En,
  it: It,
};

export function PrivacyPolicyText({ onShowPersonalData }: PrivacyPolicyTextProps) {
  const { i18n } = useTranslation();
  const locale = isSupportedLocale(i18n.language) ? i18n.language : FALLBACK_LOCALE;
  const Text = TEXTS[locale];

  return <Text onShowPersonalData={onShowPersonalData} />;
}
